<template>
    <div>
        <md-table class="custom-paginated-table">
            <md-table-row>
                <md-table-head>Label</md-table-head>
                <md-table-head>Required</md-table-head>
            </md-table-row>
            <md-table-row v-for="field in fields" :key="field.key" v-if="field.visible">
                <md-table-cell>{{ field.label }}</md-table-cell>
                <md-table-cell>
                    <input type="checkbox" v-model="field.checked" @change="markDirty" />
                </md-table-cell>
            </md-table-row>
        </md-table>
        <md-button class="md-primary" :disabled="!isDirty" @click="saveChanges">Save Changes</md-button>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { ReverseBitwise, GetBitwiseFlagsValue } from '@/utils/ApplyRatesToUtils';
import { CREATE_STOP_REQUIRED_BOOKING_FIELDS } from '@/utils/constants';
import { handleRequests, showErrorMessage } from '@/helpers';
import { GeneralMixin } from '@/mixins/GeneralMixin';

export default {
    name: 'StopRequiredBookingFields',
    mixins: [GeneralMixin],
    data() {
        return {
            fields: [
                { key: 1, label: 'Service Package', checked: false, visible: true },
                { key: 2, label: 'Stop Type', checked: false, visible: true },
                { key: 4, label: 'Source Reference', checked: false, visible: true },
                { key: 8, label: 'Contact Name', checked: false, visible: true },
                { key: 16, label: 'Phone', checked: false, visible: true },
                { key: 32, label: 'Email', checked: false, visible: true },
                { key: 64, label: 'Quantity', checked: false, visible: false }, // Set to false since visibility is defined by Capacity Settings
                { key: 128, label: 'Volume', checked: false, visible: false }, // Set to false since visibility is defined by Capacity Settings
                { key: 256, label: 'Weight', checked: false, visible: false }, // Set to false since visibility is defined by Capacity Settings
                { key: 512, label: 'Notes', checked: false, visible: true },
                { key: 1024, label: 'Trip Date', checked: false, visible: true }
            ],
            requiredFieldsBitwiseArray: [],
            isDirty: false,
            requiredBookingFieldsFlags: {
                requiredStopFieldsFlags: 0,
                requiredShipmentFieldsFlags: 0,
                requiredShipmentPickupFieldsFlags: 0,
                requiredShipmentDropoffFieldsFlags: 0
            },
            vehicleCapacityUnitsConfiguration: {}
        };
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        })
    },
    async mounted() {
        await this.initializeComponent();
    },
    methods: {
        async initializeComponent() {
            await this.getTeamData();
            await this.retrieveRequiredFlags();
        },
        async retrieveRequiredFlags() {
            this.$_handleLoaderState(true);
            const api = `/api/booking-fields-flags`;
            try {
                const response = await handleRequests(api, { method: 'get' });
                this.requiredBookingFieldsFlags = response.data;
                this.requiredFieldsBitwiseArray = ReverseBitwise(
                    this.requiredBookingFieldsFlags.requiredStopFieldsFlags,
                    CREATE_STOP_REQUIRED_BOOKING_FIELDS
                );
                this.toggleCheckedFields();
                this.$_handleLoaderState(false);
            } catch (error) {
                const message = 'Error in retrieving booking fields flags';
                showErrorMessage(this, message, error);
                this.$_handleLoaderState(false);
            }
        },
        toggleCheckedFields() {
            this.fields.forEach((field) => {
                field.checked = this.requiredFieldsBitwiseArray.includes(field.key);
            });
        },
        markDirty() {
            this.isDirty = true;
        },
        async saveChanges() {
            this.$_handleLoaderState(true);
            this.checkFieldVisibility();
            this.requiredBookingFieldsFlags.requiredStopFieldsFlags = GetBitwiseFlagsValue(this.fields);
            const api = `/api/booking-fields-flags/${this.requiredBookingFieldsFlags.bookingFieldsFlagId}`;
            const payload = {
                method: 'put',
                data: this.requiredBookingFieldsFlags
            };
            try {
                await handleRequests(api, payload);
                this.$notify({
                    message: 'Successfully updated',
                    type: 'success'
                });
                this.isDirty = false;
                this.$_handleLoaderState(false);
            } catch (error) {
                const message = 'Error in updating booking fields';
                showErrorMessage(this, message, error);
                this.$_handleLoaderState(false);
            }
        },
        async getTeamData() {
            const api = '/api/teams';
            const { data } = await handleRequests(api);
            this.vehicleCapacityUnitsConfiguration = data.data.vehicleCapacityUnitsConfiguration;
        },
        checkFieldVisibility() {
            // Makes sure the field is not required if it is not visible
            this.fields.forEach((field) => {
                if (!field.visible) {
                    field.checked = false;
                }
            });
        }
    },
    watch: {
        vehicleCapacityUnitsConfiguration: {
            handler(newVal) {
                this.fields.forEach((field) => {
                    if (newVal.some((e) => e.label === field.label)) {
                        field.visible = true;
                    }
                });
            }
        }
    }
};
</script>

<style scoped>
.custom-paginated-table {
    width: 100%;
    border-collapse: collapse;
}

input[type='checkbox'] {
    margin-left: 10px;
}
</style>
